import styled from 'styled-components';
import { DEFAULT_COMPANY_LOGO } from '@/utils/logoFallback';
import { TemplateLogoCache } from '@/utils/templateLogoCache';
import ProviderLogo from './ProviderLogo';
import { useMemo, useEffect, useCallback, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useResize from '@/hooks/useResize';

import DataTable, { TypeColumn } from '@/components/DataTable';
import { Card } from '@/layout/Card';
import { Box, Flex } from '@/layout';
import { Button } from '@/components/UI/Button';
import { Copy, Like } from '@/components/Icon';
import Loading from '@/components/UI/Loading';

import { getTemplate } from '@/api/deal.api';
import { convertLink, convertToFileSize } from '@/utils/convert';
import { openStorageDownload } from '@/utils/storageDownload';
import { isTemplateQuotaFailure } from '@/utils/templateDownloadFailure';

import { theme } from '@/theme/Theme';
import { Text } from '@/components/UI/Typography/Text';
import useCurrentLang from '@/hooks/useCurrentLang';
import { getDocTemplateV2, trackTemplateDownload } from '@/api/docTemplate.api';
import { TextField } from '@mui/material';
import { GoTop, SearchBar, TermsExampleWrapper } from '../styles';
import { H2, H3, H4 } from '@/components/UI/Typography/Heading';
import { DisclaimerModal } from '@/components/UI/Modal';
import { StatusTag } from '../../DealListItem/styles';
import { successHandler } from '@/utils/toastHandler';

type DataRow = {
  recommend?: boolean;
  name: string;
  logo: string;
  providerName: string;
  action: { name: string; path: string }[];
  websiteLink?: string;
  lawyerName?: string;
  template_category: ContractTemplateCategory | null;
  is_go_global: 0 | 1;
};

interface FilesProps {
  id?: string;
  tab_ver: boolean;
}

const CategoryButton = styled.button`
  display: block;
  width: 100%;
  min-height: 44px;
  padding: 8px 15px;
  border: 0;
  text-align: left;
  font: inherit;
  &:focus-visible { outline: 3px solid #0b319d; outline-offset: 2px; }
`;

const ProviderName = styled(Text)`
  font-size: 15px;
  line-height: 1.45;
  overflow-wrap: anywhere;
  white-space: normal;
`;

const GoGlobalTag = styled(StatusTag)`
  flex: 0 0 auto;
  width: auto;
  min-width: 104px;
  min-height: 32px;
  height: auto;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
  white-space: nowrap;
  color: ${(p) => p.theme.colors.gray500};
`;

const ContractTemplatesList = ({ id, tab_ver }: FilesProps) => {
  // @ts-ignore
  const { t } = useTranslation();
  const lang = useCurrentLang();
  const { isTablet, isMobile } = useResize();

  const tabLabel = useMemo(
    () => t('pages.dealDetail.manageFiles.contractTemplates.table.columns', { returnObjects: true }),
    [t]
  );

  const [templates, setTemplates] = useState<{ other: ContractTemplatesListProps[]; recommend: ContractTemplatesListProps[] }>();
  const [logoCache] = useState(() => new TemplateLogoCache(DEFAULT_COMPANY_LOGO));
  useEffect(() => { logoCache.retain(); return () => logoCache.release(); }, [logoCache]);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [categoryList, setCategoryList] = useState<ContractTemplateCategory[]>([]);
  const [activeCategoryId, setActiveCategoryId] = useState<number>(0);

  const { resetField, control } = useForm<{ category: string }>();
  const searchTerm = useWatch({ control, name: 'category', defaultValue: '' });
  const [showTermsExampleTab, setShowTermsExampleTab] = useState(false);
  const [showTermsSection, setShowTermsSection] = useState<'resolution' | 'law'>('resolution');
  const [isLoading, setIsLoading] = useState(true);
  const [downloadError, setDownloadError] = useState('');

  // 下載連結
  const downloadFile = useCallback(async (name: string, path: string) => {
    setDownloadError('');
    try {
      await openStorageDownload(path);
    } catch (error) {
      setDownloadError(isTemplateQuotaFailure(error)
        ? lang === 'en' ? 'Your template download allowance is used up. Free includes 3 downloads per 12-month period.' : '合約範本下載額度已用完。Free 每 12 個月週期包含 3 次下載。'
        : lang === 'en' ? 'Unable to download this template. Please retry.' : '無法下載此合約範本，請重試。');
      return;
    }

    // 下載成功後，非同步追蹤下載次數
    try {
      await trackTemplateDownload();
    } catch (err) {
      console.warn('下載追蹤失敗:', err);
    }
  }, [lang]);

  // 複製到剪貼簿（支援富文本格式）
  const copyToClipboard = useCallback(async (plainText: string, htmlText?: string) => {
    try {
      if (htmlText && navigator.clipboard.write) {
        // 提供純文字和 HTML 格式
        const clipboardItem = new ClipboardItem({
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
          'text/html': new Blob([htmlText], { type: 'text/html' }),
        });
        await navigator.clipboard.write([clipboardItem]);
      } else {
        // 降級為純文字複製
        await navigator.clipboard.writeText(plainText);
      }
      successHandler(t('success.copied'));
    } catch (err) {
      console.error('複製失敗:', err);
      // 如果現代 API 失敗，嘗試舊方法
      const textArea = document.createElement('textarea');
      textArea.value = plainText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }, [t]);

  const initData = useCallback(async () => {
    try {
      setIsLoading(true);
      const categoryListRes = (await getDocTemplateV2()).data;
      // 過濾 is_active 為 1 或 true，並依 sort_order 由小到大排序
      const activeCategory = categoryListRes.data
        .filter((category: ContractTemplateCategory) => category.is_active === 1)
        .sort((a: ContractTemplateCategory, b: ContractTemplateCategory) => a.sort_order - b.sort_order);
      setCategoryList(activeCategory);

      const res = (await getTemplate(id)).data;
      setTemplates(res.data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    initData();
  }, [initData]);

  const docTemplate = useMemo(() => {
    if (!templates) return undefined;
    const other: DataRow[] = templates.other.map((data) => ({
      recommend: false,
      name:
        data.en_file && lang === 'en'
          ? data.en_file.name
          : data.sc_file && lang === 'sc'
            ? data.sc_file.name
            : data.tc_file && lang === 'tc'
              ? data.tc_file.name
              : data.en_file
                ? data.en_file.name
                : data.sc_file
                  ? data.sc_file.name
                  : data.tc_file
                    ? data.tc_file.name
                    : '',
      action: [
        data.en_file && {
          name: data.en_file.name,
          path: data.en_file.path,
        },
        data.sc_file && {
          name: data.sc_file.name,
          path: data.sc_file.path,
        },
        data.tc_file && {
          name: data.tc_file.name,
          path: data.tc_file.path,
        },
      ],
      logo: data.provider ? data.provider.logo_url ?? '' : data.institution_logo ?? '',
      providerName: data.provider ? data.provider.name : data.institution ?? '',
      websiteLink: data.provider ? data.provider.website ?? undefined : data.website_link,
      lawyerName: data.lawyer_name,
      template_category: data.template_category,
      is_go_global: data.is_go_global,
    }));
    const recommend: DataRow[] = templates.recommend.map((data) => ({
      recommend: true,
      name:
        data.en_file && lang === 'en'
          ? data.en_file.name
          : data.sc_file && lang === 'sc'
            ? data.sc_file.name
            : data.tc_file && lang === 'tc'
              ? data.tc_file.name
              : data.en_file
                ? data.en_file.name
                : data.sc_file
                  ? data.sc_file.name
                  : data.tc_file
                    ? data.tc_file.name
                    : '',
      action: [
        data.en_file && {
          name: data.en_file.name,
          path: data.en_file.path,
        },
        data.sc_file && {
          name: data.sc_file.name,
          path: data.sc_file.path,
        },
        data.tc_file && {
          name: data.tc_file.name,
          path: data.tc_file.path,
        },
      ],
      logo: data.provider ? data.provider.logo_url ?? '' : data.institution_logo ?? '',
      providerName: data.provider ? data.provider.name : data.institution ?? '',
      websiteLink: data.provider ? data.provider.website ?? undefined : data.website_link,
      lawyerName: data.lawyer_name,
      template_category: data.template_category,
      is_go_global: data.is_go_global,
    }));

    // 將所有範本資料依 template_category.id 分組
    const allTemplates = [...other, ...recommend];
    const groupedByCategory: Record<string, DataRow[]> = {};

    allTemplates.forEach((item) => {
      const catId = item.template_category?.id?.toString() || 'unknown';
      if (!groupedByCategory[catId]) {
        groupedByCategory[catId] = [];
      }
      groupedByCategory[catId].push(item);
    });

    return groupedByCategory;
  }, [templates, lang]);

  // 先取得有資料的分類（用於右側顯示）
  const categoriesWithData = useMemo(() => {
    if (!docTemplate) return [];
    return categoryList.filter((cat) => {
      const categoryData = docTemplate[cat.id.toString()];
      return categoryData && categoryData.length > 0;
    });
  }, [categoryList, docTemplate]);

  // 根據搜尋框內容過濾左側分類列表，tab_ver為true時只搜索分類名稱，為false時同時搜索分類名稱和檔案名稱
  const filteredCategories = useMemo(() => {
    if (!searchTerm || !searchTerm.trim()) {
      return categoriesWithData;
    }

    const searchLower = searchTerm.toLowerCase().trim();

    return categoriesWithData.filter((cat) => {
      // 檢查分類名稱是否匹配
      if (cat.name.toLowerCase().includes(searchLower)) {
        return true;
      }

      // 如果是 tab 版本，只搜索分類名稱
      if (tab_ver) {
        return false;
      }

      // 非 tab 版本時，檢查該分類下的任何檔案名稱是否匹配
      const categoryData = docTemplate?.[cat.id.toString()];
      if (categoryData) {
        return categoryData.some((file) => file.name.toLowerCase().includes(searchLower));
      }

      return false;
    });
  }, [categoriesWithData, searchTerm, docTemplate, tab_ver]);

  // 滾動監聽，判斷當前顯示的分類
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200; // 加上 offset 讓切換更早觸發

      for (let i = filteredCategories.length - 1; i >= 0; i--) {
        const cat = filteredCategories[i];
        const element = document.getElementById(`category-${cat.id}`);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveCategoryId(i);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // 初始化時執行一次

    return () => window.removeEventListener('scroll', handleScroll);
  }, [filteredCategories]);

  const columns: ColumnDef<DataRow>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: tabLabel[0],
        cell: ({ row }) => (
          <Flex alignItems={'center'} gridGap={'10px'} width={tab_ver ? '200px' : '400px'}>
            <Text style={{ wordBreak: 'break-word' }}>
              {row.original.recommend && <Like.LikeSVG fill={theme.colors.orange} />} {row.original.name.replace(/\.[^.]+$/, '').replace(/_/g, ' ')}
            </Text>
          </Flex>
        ),
      },
      {
        accessorKey: 'action',
        header: tabLabel[4],
        cell: ({ row }) => (
          <Flex gridGap={'10px'} width={'210px'}>
            {row.original.action.map(
              (file, index) =>
                file && (
                  <Button
                    key={`link-${index}`}
                    size={'md'}
                    variant={'primary'}
                    onClick={() => {
                      downloadFile(file.name, file.path);
                    }}
                  >
                    {index === 0 ? 'EN' : index === 1 ? '簡' : '繁'}
                  </Button>
                )
            )}
          </Flex>
        ),
      },
      {
        accessorKey: 'providedBy',
        header: tabLabel[5],
        cell: ({ row }) => (
          <Flex gridGap={'12px'} flexWrap={'wrap'} alignItems={'center'} width={tab_ver ? 'unset' : '350px'}>
            <Flex flexDirection={'column'} gridGap={'6px'} flex={'1 1 200px'} minWidth={0}>
              {
                !row.original.websiteLink && (
                  <>
                    {row.original.logo && <ProviderLogo src={row.original.logo} name={row.original.providerName} cache={logoCache} />}
                    {row.original.providerName && <ProviderName>{row.original.providerName}</ProviderName>}
                    {row.original.lawyerName && <Text style={{ width: tab_ver ? '100%' : 'unset' }}>{row.original.lawyerName}</Text>}
                  </>
                )
              }
              {row.original.websiteLink && (
                <Box width={'100%'}>
                  <a
                    target="_blank"
                    href={row.original.websiteLink}
                    rel="noreferrer"
                    style={{
                      color: theme.colors.primary300,
                    }}
                  >
                    {row.original.logo && <ProviderLogo src={row.original.logo} name={row.original.providerName} cache={logoCache} />}
                    {row.original.providerName && <ProviderName>{row.original.providerName}</ProviderName>}
                    {row.original.lawyerName && <Text style={{ width: tab_ver ? '100%' : 'unset', textDecoration: 'underline' }}>{row.original.lawyerName}</Text>}
                  </a>
                </Box>
              )}
            </Flex>
            {
              row.original.is_go_global === 1 && (
                <GoGlobalTag variant={'completed'}>{t('info.goGlobalTag')}</GoGlobalTag>
              )
            }
          </Flex>
        ),
      },
    ],
    [t, tabLabel, tab_ver, downloadFile, logoCache]
  );

  return (
    <Card marginTop={isMobile ? 20 : 0}>
      {downloadError && <Text role="alert" color={theme.colors.error}>{downloadError}</Text>}
      <DisclaimerModal showModal={showDisclaimerModal} setShowModal={setShowDisclaimerModal} />
        <Flex flexDirection={isMobile ? 'column' : 'row'} gridGap={30} overflow={'auto'}>
          <Box
            maxWidth={isMobile ? 'unset' : 280}
            width={isMobile ? '100%' : '30%'}
            height={'min-content'}
            position={isMobile ? 'relative' : 'sticky'}
            top={isMobile ? 0 : 20}
            style={{ flexShrink: 0 }}
          >
            <SearchBar
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <Controller
                control={control}
                defaultValue=""
                name={'category'}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <div style={{ width: '100%' }}>
                    <TextField
                      label={t('info.searchContractOrCategory')}
                      placeholder={t('info.searchContractOrCategory')}
                      variant="outlined"
                      value={value}
                      sx={{ width: '100%', background: '#FFF', borderRadius: '5px 0 0 5px' }}
                      onChange={onChange}
                      size="small"
                      error={!!error}
                      helperText={error ? error.message : null}
                      autoComplete="off"
                    />
                  </div>
                )}
              />
            </SearchBar>
            <Box>
              <CategoryButton
                type="button"
                aria-pressed={showTermsExampleTab && showTermsSection === 'resolution'}
                onClick={() => {
                  setShowTermsExampleTab(true);
                  setShowTermsSection('resolution');
                }}
                style={{
                  cursor: 'pointer',
                  backgroundColor: showTermsExampleTab && showTermsSection === 'resolution' ? theme.colors.primary300 : 'transparent',
                  transition: 'background-color 0.2s ease',
                }}
              >
                <H4 color={showTermsExampleTab && showTermsSection === 'resolution' ? theme.colors.light : theme.colors.primary300}>
                  {t('exampleTerms.type.resolution')}
                </H4>
              </CategoryButton>
              <CategoryButton
                type="button"
                aria-pressed={showTermsExampleTab && showTermsSection === 'law'}
                onClick={() => {
                  setShowTermsExampleTab(true);
                  setShowTermsSection('law');
                }}
                style={{
                  cursor: 'pointer',
                  backgroundColor: showTermsExampleTab && showTermsSection === 'law' ? theme.colors.primary300 : 'transparent',
                  transition: 'background-color 0.2s ease',
                }}
              >
                <H4 color={showTermsExampleTab && showTermsSection === 'law' ? theme.colors.light : theme.colors.primary300}>
                  {t('exampleTerms.type.laws')}
                </H4>
              </CategoryButton>
              {filteredCategories.map((cat, index) => (
                <CategoryButton
                  type="button"
                  key={cat.id}
                  aria-pressed={!showTermsExampleTab && activeCategoryId === index}
                  onClick={() => {
                    setShowTermsExampleTab(false);
                    setActiveCategoryId(index);
                    const element = document.getElementById(`category-${cat.id}`);
                    if (!tab_ver && element) {
                      element.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
                    }
                  }}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: !showTermsExampleTab && activeCategoryId === index ? theme.colors.primary300 : 'transparent',
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <H4 color={!showTermsExampleTab && activeCategoryId === index ? theme.colors.light : theme.colors.primary500}>
                    {lang === 'en' ? cat.name : lang === 'sc' ? cat.name_sc : cat.name_tw}
                  </H4>
                </CategoryButton>
              ))}
            </Box>
          </Box>
          {
            !showTermsExampleTab && (
              <Box flex={1} minWidth={0}>
                <Box marginBottom={30}>
                  <Text fontWeight={900} marginBottom={10} color={theme.colors.primary500}>
                    {t('pages.dealDetail.manageFiles.contractTemplates.title')}
                  </Text>
                  <Text style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                    {t('pages.dealDetail.manageFiles.contractTemplates.content.context')}{' '}
                    <button type="button"
                      style={{ cursor: 'pointer', color: theme.colors.primary500, background: 'none', border: 0, padding: 0, font: 'inherit', textDecoration: 'underline' }}
                      onClick={() => setShowDisclaimerModal(true)}
                    >
                      {t('pages.dealDetail.manageFiles.contractTemplates.content.Disclaimer.title')}
                    </button>
                    {lang === 'en' ? '.' : '。'}
                  </Text>
                </Box>
                <Box flex={1}>
                  {/* 依分類顯示，每個分類下有自己的 DataTable */}
                  {isLoading && <Loading />}
                  {!isLoading &&
                    filteredCategories.length > 0 &&
                    docTemplate &&
                    filteredCategories
                      .filter((cat, index) => (tab_ver ? index === activeCategoryId : true))
                      .map((cat) => {
                        const categoryData = docTemplate[cat.id.toString()] || [];
                        // 只顯示有資料的分類
                        if (categoryData.length === 0) return null;

                        return (
                          <Box key={cat.id} id={`category-${cat.id}`} marginBottom={30}>
                            <Text fontWeight={900} fontSize={24} marginBottom={10} color={theme.colors.primary500}>
                              {lang === 'en' ? cat.name : lang === 'sc' ? cat.name_sc : cat.name_tw}
                            </Text>
                            <DataTable columns={columns} data={categoryData} perPage={categoryData.length} hidePagination />
                          </Box>
                        );
                      })}
                  {!isLoading && filteredCategories.length === 0 && (
                    <Box padding={'30px 0'}>
                      <Text
                        textAlign={'center'}
                        fontSize={theme.fontSizes.xl}
                        color={theme.colors.primary500}
                        fontWeight={800}
                      >
                        {t('error.noData')}
                      </Text>
                    </Box>
                  )}
                </Box>
              </Box>
            )
          }
          {
            showTermsExampleTab && showTermsSection === 'resolution' && (
              <Box flex={1}>
                <Box marginBottom={30}>
                  <Text fontWeight={900} fontSize={24} marginBottom={20} color={theme.colors.primary500}>
                    {t('exampleTerms.resolution.title')}
                  </Text>
                  <Text>
                    {t('exampleTerms.resolution.content.1').split('\n').map((line, i) => (
                      <span key={i}>{line}<br /></span>
                    ))}
                  </Text>
                  <TermsExampleWrapper>
                    <Flex justifyContent={'space-between'} alignItems={'center'} marginBottom={'20px'}>
                      <H3 fontWeight={'bold'}>
                        {t('exampleTerms.resolution.terms.1.title')}
                      </H3>
                      <Button
                        size={'table'}
                        variant={'primary'}
                        style={{ padding: '20px', fontWeight: 'normal' }}
                        onClick={() => {
                          const title = t('exampleTerms.resolution.terms.1.title');
                          const content = t('exampleTerms.resolution.terms.1.content');
                          const plainText = `${title}\n${content}`;
                          const htmlText = `<strong>${title}</strong><br/>${content.replace(/\n/g, '<br/>')}`;
                          copyToClipboard(plainText, htmlText);
                        }}
                      >
                        <Flex gridGap={'10px'} alignItems={"center"}>
                          <Copy.CopySVG fill={theme.colors.light} />
                          {t('buttons.copy')}
                        </Flex>
                      </Button>
                    </Flex>
                    <Text>
                      {t('exampleTerms.resolution.terms.1.content').split('\n').map((line, i) => (
                        <span key={i}>{line}<br /></span>
                      ))}
                    </Text>
                  </TermsExampleWrapper>
                  <Text>
                    {t('exampleTerms.resolution.content.2').split('\n').map((line, i) => (
                      <span key={i}>{line}<br /></span>
                    ))}
                  </Text>
                  <TermsExampleWrapper>
                    <Flex justifyContent={'space-between'} alignItems={'center'} marginBottom={'20px'}>
                      <H3 fontWeight={'bold'}>
                        {t('exampleTerms.resolution.terms.2.title')}
                      </H3>
                      <Button
                        size={'table'}
                        variant={'primary'}
                        style={{ padding: '20px', fontWeight: 'normal' }}
                        onClick={() => {
                          const title = t('exampleTerms.resolution.terms.2.title');
                          const content = t('exampleTerms.resolution.terms.2.content');
                          const plainText = `${title}\n${content}`;
                          const htmlText = `<strong>${title}</strong><br/>${content.replace(/\n/g, '<br/>')}`;
                          copyToClipboard(plainText, htmlText);
                        }}
                      >
                        <Flex gridGap={'10px'} alignItems={"center"}>
                          <Copy.CopySVG fill={theme.colors.light} />
                          {t('buttons.copy')}
                        </Flex>
                      </Button>
                    </Flex>
                    <Text>
                      {t('exampleTerms.resolution.terms.2.content').split('\n').map((line, i) => (
                        <span key={i}>{line}<br /></span>
                      ))}
                    </Text>
                  </TermsExampleWrapper>
                  <TermsExampleWrapper>
                    <Flex justifyContent={'space-between'} alignItems={'center'} marginBottom={'20px'}>
                      <H3 fontWeight={'bold'}>
                        {t('exampleTerms.resolution.terms.3.title')}
                      </H3>
                      <Button
                        size={'table'}
                        variant={'primary'}
                        style={{ padding: '20px', fontWeight: 'normal' }}
                        onClick={() => {
                          const title = t('exampleTerms.resolution.terms.3.title');
                          const content = t('exampleTerms.resolution.terms.3.content');
                          const plainText = `${title}\n${content}`;
                          const htmlText = `<strong>${title}</strong><br/>${content.replace(/\n/g, '<br/>')}`;
                          copyToClipboard(plainText, htmlText);
                        }}
                      >
                        <Flex gridGap={'10px'} alignItems={"center"}>
                          <Copy.CopySVG fill={theme.colors.light} />
                          {t('buttons.copy')}
                        </Flex>
                      </Button>
                    </Flex>
                    <Text>
                      {t('exampleTerms.resolution.terms.3.content').split('\n').map((line, i) => (
                        <span key={i}>{line}<br /></span>
                      ))}
                    </Text>
                  </TermsExampleWrapper>
                </Box>
              </Box>
            )
          }
          {
            showTermsExampleTab && showTermsSection === 'law' && (
              <Box flex={1}>
                <Box marginBottom={30}>
                  <Text fontWeight={900} fontSize={24} marginBottom={20} color={theme.colors.primary500}>
                    {t('exampleTerms.laws.title')}
                  </Text>
                  <TermsExampleWrapper>
                    <Flex justifyContent={'space-between'} alignItems={'center'} marginBottom={'20px'}>
                      <H3 fontWeight={'bold'}>
                        {t('exampleTerms.laws.terms.1.title')}
                      </H3>
                      <Button
                        size={'table'}
                        variant={'primary'}
                        style={{ padding: '20px', fontWeight: 'normal' }}
                        onClick={() => {
                          const content = t('exampleTerms.laws.terms.1.content');
                          copyToClipboard(content);
                        }}
                      >
                        <Flex gridGap={'10px'} alignItems={"center"}>
                          <Copy.CopySVG fill={theme.colors.light} />
                          {t('buttons.copy')}
                        </Flex>
                      </Button>
                    </Flex>
                    <Text>
                      {t('exampleTerms.laws.terms.1.content')}
                    </Text>
                  </TermsExampleWrapper>
                </Box>
              </Box>
            )
          }
        </Flex>
      {!tab_ver && (
        <GoTop
          isTablet={isTablet}
          isMobile={isMobile}
          onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          {t('top')}
        </GoTop>
      )}
    </Card>
  );
};

export default ContractTemplatesList;
